/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import cloneDeep from 'lodash/cloneDeep';
import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import isPlainObject from 'lodash/isPlainObject';
import forEach from 'lodash/forEach';
import reduce from 'lodash/reduce';
import lodashSet from 'lodash/set';
import lodashGet from 'lodash/get';
import { toTitleCase } from 'sarsaparilla';

const zeroValueDateString = '0001-01-01T00:00:00Z';

export function isEmpty(str) {
    return !str || str.length === 0;
}

// This is needed to compensate for fetch's lack of url parameter management without string interpolation
export function urlWithParams(urlString, params = {}) {
    let searchParams = '';
    Object.keys(params).forEach((key) => {
        let sep = '';
        if (searchParams !== '') {
            sep = '&';
        }
        if (params[key] || params[key] === 0) {
            const val = params[key];
            if (!isEmpty(val) || val === 0) {
                if (Array.isArray(val)) {
                    val.forEach((v) => {
                        if (searchParams !== '') {
                            sep = '&';
                        }
                        searchParams = `${searchParams + sep + key}=${encodeURIComponent(v)}`;
                    });
                } else {
                    searchParams = `${searchParams + sep + key}=${encodeURIComponent(val)}`;
                }
            }
        }
    });
    let ret = urlString;
    if (!isEmpty(searchParams)) {
        ret = `${ret}?${searchParams}`;
    }
    return ret;
}

// fetch doesn't handle errors well
export const status = (response) => {
    if (response.status >= 200 && response.status < 300) {
        return Promise.resolve(response);
    }
    const err = new Error(response.statusText);
    err.status = response.status;
    return Promise.reject(err);
};

export const catchError = (error) =>
    // comment out logging to debug tests
    // logging.error(error.message);
    ({ data: { err: error.message, status: error.status } });

// parse fetch response to json
export function json(response) {
    return response.json();
}

export const requestFailure = (type, error) => ({
    type,
    error,
});

export const requestSuccess = (type, payload) => ({
    type,
    payload,
});

export const transformCampgroundFromBackEnd = (campground) => {
    const facility = cloneDeep(campground);
    const obj = {};
    facility.activities = (campground.activities || []).reduce((tmp, activity) => {
        obj[toTitleCase(activity.activity_name)] = toTitleCase(activity.activity_name);
        return obj;
    }, {});
    if (campground.notices) {
        facility.notices = { info: [], warning: [], alert: [] };
        for (const notice of campground.notices) {
            if (notice) {
                const noticeType = notice.notice_type;
                if (lodashGet(facility, `notices.${noticeType}`, null)) {
                    facility.notices[notice.notice_type].push(notice);
                }
            }
        }
    }
    // Change the null date value back to null
    if (campground.important_dates && campground.important_dates.length > 0) {
        facility.important_dates = campground.important_dates.map((item) => {
            const newItem = { ...item };
            if (item.start_date === zeroValueDateString) {
                newItem.start_date = null;
            }
            if (item.end_date === zeroValueDateString) {
                newItem.end_date = null;
            }
            return newItem;
        });
    }
    return facility;
};

export const transformCampgroundToBackEnd = (pendingCampground) => {
    const facility = cloneDeep(pendingCampground);
    facility.activities = Object.entries(pendingCampground.activities).map(
        ([key, value]) => ({ activity_name: key, activity_description: value })
    );

    if (pendingCampground.notices) {
        // append all the list together before submission
        const noticesArray = lodashGet(pendingCampground, 'notices', []);

        const finalNoticesArray = reduce(
            noticesArray,
            (result, noticesByType) => [...result, ...noticesByType],
            []
        );

        lodashSet(facility, 'notices', finalNoticesArray);
    }
    return facility;
};

export const transformGatewayFromBackEnd = (recarea) => {
    const gateway = cloneDeep(recarea);
    const obj = {};
    gateway.activities = (gateway.activities || []).reduce((tmp, activity) => {
        obj[toTitleCase(activity.activity_name)] = toTitleCase(activity.activity_name);
        return obj;
    }, {});
    if (recarea.notices) {
        gateway.notices = { info: [], warning: [], alert: [] };
        for (const notice of recarea.notices) {
            if (gateway.notices[notice.notice_type]) {
                gateway.notices[notice.notice_type].push(notice);
            }
        }
    }
    return gateway;
};

export const transformGatewayToBackEnd = (recarea) => {
    const gateway = cloneDeep(recarea);
    gateway.activities = Object.entries(recarea.activities).map(([key, value]) => ({
        activity_name: key,
        activity_description: value,
    }));
    return gateway;
};

export const convertToDegrees = (decimal, isLongitude) => {
    const abs = Math.abs(decimal);
    const d = Math.floor(abs);
    const minfloat = (abs - d) * 60;
    const m = Math.floor(minfloat);
    const secfloat = (minfloat - m) * 60;
    const s = secfloat.toFixed(4);

    let cardinalPos = 'N';
    let cardinalNeg = 'S';

    if (isLongitude) {
        cardinalPos = 'E';
        cardinalNeg = 'W';
    }

    let cardinal = cardinalPos;
    if (decimal < 0) {
        cardinal = cardinalNeg;
    }

    return `${d}° ${m}' ${s}" ${cardinal}`;
};

export function scrollToElement(id, defaultId) {
    const elementId = isNil(id) ? defaultId : id;
    const targetElement = document.getElementById(elementId);
    const page = document.getElementById('page-body');
    if (!isNil(targetElement)) {
        targetElement.scrollIntoView(true);

        const scrolledY = window.scrollY;

        if (page?.offsetTop && scrolledY) {
            const scrollPixels = scrolledY - page.offsetTop;
            window.scroll(0, scrollPixels);
        }
    }
}

export const getNoticeListByType = (campsite, type) => {
    const list = [];
    const notices = lodashGet(campsite, 'notices', null);
    if (notices) {
        forEach(notices, (notice) => {
            if (isEqual(notice.notice_type, type)) {
                list.push(notice.notice_text);
            }
        });
    }
    return list;
};

export const isMobile = {
    Android() {
        return window.navigator.userAgent.match(/Android/i);
    },
    BlackBerry() {
        return window.navigator.userAgent.match(/BlackBerry/i);
    },
    iOS() {
        return window.navigator.userAgent.match(/iPhone|iPad|iPod/i);
    },
    Opera() {
        return window.navigator.userAgent.match(/Opera Mini/i);
    },
    Windows() {
        return window.navigator.userAgent.match(/IEMobile/i);
    },
    any() {
        return (
            isMobile.Android() ||
            isMobile.BlackBerry() ||
            isMobile.iOS() ||
            isMobile.Opera() ||
            isMobile.Windows()
        );
    },
};

export const printPDF = (blob, fileName) => {
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        // IE opens blobs this way:
        if (fileName) {
            window.navigator.msSaveOrOpenBlob(blob, fileName);
        } else {
            window.navigator.msSaveOrOpenBlob(blob);
        }
    } else {
        const url = window.URL.createObjectURL(blob);
        if (isMobile.any()) {
            const reader = new FileReader();
            reader.onload = () => {
                window.location.href = reader.result;
            };
            reader.readAsDataURL(blob);
        } else {
            const downloadLink = document.createElement('a');
            downloadLink.target = '_blank';
            downloadLink.download = fileName;

            downloadLink.href = url;
            downloadLink.style.display = 'none';

            document.body.append(downloadLink);

            downloadLink.click();

            document.body.removeChild(downloadLink);
            window.URL.revokeObjectURL(url);
        }
    }
};

export const isValidUUID = (value) => {
    return (
        !isNil(value) &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(
            value
        )
    );
};

export function getDayOfWeekShortName(longName) {
    switch (longName) {
        case 'Monday':
            return 'mon';
        case 'Tuesday':
            return 'tue';
        case 'Wednesday':
            return 'wed';
        case 'Thursday':
            return 'thu';
        case 'Friday':
            return 'fri';
        case 'Saturday':
            return 'sat';
        case 'Sunday':
            return 'sun';
        default:
            return 'Invalid';
    }
}

export function flattenObject(obj = {}, includeArrays = true) {
    const result = {};

    const flatten = (collection, prefix = '', suffix = '') => {
        forEach(collection, (value, key) => {
            const path = `${prefix}${key}${suffix}`;

            if (isArray(value)) {
                if (includeArrays) {
                    flatten(value, `${path}[`, ']');
                } else {
                    result[key] = value;
                }
            } else if (isPlainObject(value)) {
                flatten(value, `${path}.`);
            } else {
                result[path] = value;
            }
        });
    };

    flatten(obj);

    return result;
}
